import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import SellerNavbar from "../components/SellerNavbar";
import "./SellerDashboard.css";

export default function SellerDashboard() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Seller Dashboard | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Manage your store on Balitah, a local Bruneian e-commerce platform for sellers.",
    );
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await api.getMyProducts();
      setProducts(data.data || []);
    } catch (error) {
      console.error("Failed to load products:", error);
      alert("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const deleteProduct = async (id, name) => {
    const shouldDelete = window.confirm(
      `Delete product \"${name}\"? This action cannot be undone.`,
    );

    if (!shouldDelete) {
      return;
    }

    try {
      await api.deleteProduct(id);
      await fetchProducts();
    } catch (error) {
      console.error("Failed to delete product:", error);
      alert("Failed to delete product");
    }
  };

  const getDisplayPrice = (product) => {
    const basePrice = Number(product?.price);

    if (Number.isFinite(basePrice) && basePrice > 0) {
      return basePrice;
    }

    const variantPrices = Array.isArray(product?.variants)
      ? product.variants
          .map((variant) => Number(variant?.price))
          .filter((price) => Number.isFinite(price) && price >= 0)
      : [];

    if (variantPrices.length > 0) {
      return Math.min(...variantPrices);
    }

    return 0;
  };

  return (
    <div className="sd-page">
      <SellerNavbar />

      <div className="sd-container">
        {/* Header */}
        <div className="sd-header">
          <div className="sd-header-content">
            <h1>My Products</h1>
            <p>Manage and add new products to your store</p>
          </div>
          <div className="sd-header-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
          </div>
        </div>

        {/* Products Section */}
        <section className="sd-section">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <h2 className="sd-section-title">Products</h2>
            <button
              className="sd-btn sd-btn-primary"
              onClick={() => navigate("/seller/add")}
            >
              + Add New Product
            </button>
          </div>

          {loading ? (
            <div className="sd-loading">
              <div className="sd-spinner"></div>
              <p>Loading your products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="sd-empty">
              <div className="sd-empty-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                </svg>
              </div>
              <h2>No products yet</h2>
              <p>Start by adding your first product to your store</p>
            </div>
          ) : (
            <div className="sd-products-grid">
              {products.map((p) => (
                <div key={p._id} className="sd-product-card">
                  <div className="sd-product-image">
                    {Array.isArray(p.images) && p.images.length > 0 ? (
                      <img
                        src={
                          typeof p.images[0] === "string"
                            ? p.images[0]
                            : p.images[0]?.url
                        }
                        alt={p.name}
                      />
                    ) : p.image ? (
                      <img src={p.image} alt={p.name} />
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}
                      >
                        Product Image
                      </div>
                    )}
                  </div>

                  <div className="sd-product-info">
                    <h3 className="sd-product-name">{p.name}</h3>
                    <div className="sd-product-price">
                      BND {getDisplayPrice(p).toFixed(2)}
                    </div>

                    <div className="sd-product-actions">
                      <button
                        className="sd-btn sd-btn-in-card sd-btn-primary"
                        onClick={() => navigate(`/seller/edit/${p._id}`)}
                      >
                        Edit
                      </button>
                      <button
                        className="sd-btn sd-btn-in-card sd-btn-danger"
                        onClick={() => deleteProduct(p._id, p.name)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
